/**
 * Cluster topology — EDG VoIP (13 VPS Seeweb).
 * Sorgente unica per mappa interattiva, side panel, tabella PDF, export JSON.
 * Allineato 1:1 alla tabella nodi in `docs/runbooks/cluster-topology.md`
 * (replit.md rimanda al runbook per non duplicare).
 *
 * Aggiornamento manuale: quando cambia la tabella nodi nel runbook
 * (nuovi nodi, IP, ruoli), aggiornare qui di conseguenza. Nessuna
 * sincronizzazione automatica con il backend.
 */

import { getDefaultCrmOrigin, getDefaultMasterOrigin, PRODUCT_REGISTRY } from '@easyflow/shared';

export type TierId = 'app' | 'pbx' | 'sbc' | 'db' | 'cache' | 'video' | 'obs';

export type NodeStatus = 'leader' | 'replica' | 'ha' | 'single';

export interface ClusterTier {
  id: TierId;
  label: string;
  description: string;
  color: string;
}

export interface ClusterNode {
  id: string;
  label: string;
  tier: TierId;
  role: string;
  publicIp: string | null;
  exposure: 'public' | 'management-only' | 'lan-only';
  lanIp: string | null;
  sshSecret: string;
  status: NodeStatus;
  services: string[];
  runbooks: string[];
  notes?: string;
}

export type EdgeKind =
  | 'replication'
  | 'app-traffic'
  | 'control-plane'
  | 'media'
  | 'cache'
  | 'etcd'
  | 'sip'
  | 'esl';

export interface ClusterEdge {
  id: string;
  from: string;
  to: string;
  kind: EdgeKind;
  label: string;
}

export interface ArchitectureConstraint {
  title: string;
  detail: string;
}

export interface RunbookPointer {
  title: string;
  path: string;
}

export interface ClusterTopology {
  generatedFor: string;
  version: string;
  backendRuntime: { nodeIds: string[]; workersPerNode: number; ports: number[] };
  tiers: ClusterTier[];
  nodes: ClusterNode[];
  edges: ClusterEdge[];
  edgeLegend: { kind: EdgeKind; label: string; color: string; dash?: string }[];
  statusLegend: { status: NodeStatus; label: string }[];
  stack: string[];
  constraints: ArchitectureConstraint[];
  runbookIndex: RunbookPointer[];
  publicEndpoints: { label: string; value: string }[];
}

export const CLUSTER_TOPOLOGY: ClusterTopology = {
  generatedFor: 'EDG VoIP — cluster prod Seeweb',
  version: '2026-10-07',
  backendRuntime: {
    nodeIds: ['be1', 'be2'],
    workersPerNode: 8,
    ports: Array.from({ length: 8 }, (_, index) => 3000 + index),
  },
  tiers: [
    {
      id: 'app',
      label: 'App tier',
      description: 'LB-1 edge web pubblico + FE-1 edge statico interno + backend Node PM2 (BE-1 + BE-2)',
      color: '#6366f1',
    },
    {
      id: 'pbx',
      label: 'PBX tier',
      description: 'FreeSWITCH cluster (FS-1 + FS-2) sticky-tenant',
      color: '#0ea5e9',
    },
    {
      id: 'sbc',
      label: 'SBC tier',
      description: 'Kamailio SBC (KAM-1) — dispatcher SIP + WSS WebRTC',
      color: '#a855f7',
    },
    {
      id: 'db',
      label: 'DB tier',
      description: 'PostgreSQL 16 Patroni HA (leader+replica)',
      color: '#10b981',
    },
    {
      id: 'cache',
      label: 'Cache tier',
      description: 'Redis dedicato; membro del quorum etcd con DB-1 e DB-2',
      color: '#f59e0b',
    },
    {
      id: 'video',
      label: 'Video tier',
      description: 'LiveKit self-hosted (SFU+TURN) in HA',
      color: '#ec4899',
    },
    {
      id: 'obs',
      label: 'Observability tier',
      description: 'Bastion SSH + monitoring (OBS-1)',
      color: '#64748b',
    },
  ],
  nodes: [
    {
      id: 'lb1',
      label: 'LB-1',
      tier: 'app',
      role: 'Edge web pubblico: TLS termination + load balancing per tutti gli hostname web (easyflowai + w3suite), certbot unico motore di rinnovo',
      publicIp: '85.94.215.110',
      exposure: 'public',
      lanIp: '10.4.202.62',
      sshSecret: 'SSH_KEY_LB_B64',
      status: 'single',
      services: [
        'nginx (TLS web, API e Socket.IO; pool backend a 16 endpoint)',
        'certbot (rinnovo cert, deploy-hook reload nginx)',
        'node_exporter (metriche LAN + stato rinnovo certificati)',
        'fail2ban + agent (jail nginx + sshd)',
      ],
      runbooks: ['lb-vps-setup.md', 'cluster-topology.md', 'fail2ban-cluster.md'],
      notes: 'Ingresso web pubblico: statici verso FE-1 :8081, API/Socket.IO verso BE-1 e BE-2 :3000–3007. Grafana, GlitchTip e DB studio raggiunti attraverso LB-1 verso OBS-1 LAN. Nessun processo Node su LB-1.',
    },
    {
      id: 'fe1',
      label: 'FE-1',
      tier: 'app',
      role: 'Origin statico interno: nginx :8081 LAN serve i frontend a LB-1; nessun backend Node',
      publicIp: '85.94.215.99',
      exposure: 'management-only',
      lanIp: '10.4.202.50',
      sshSecret: 'SSH_KEY_FE1_B64',
      status: 'single',
      services: [
        'nginx :8081 LAN (dist tenant, master, W3 e Brand)',
        'agent shell (fail2ban heartbeat + system metrics push)',
        'fail2ban + agent',
      ],
      runbooks: ['gotchas-deploy.md', 'fail2ban-cluster.md', 'cluster-topology.md'],
      notes: 'IP WAN mantenuto per gestione, non più in DNS web. Il traffico pubblico entra da LB-1. Certbot e PM2 disabilitati; reload nginx tramite account efai_edge con ForceCommand.',
    },
    {
      id: 'fs1',
      label: 'FS-1',
      tier: 'pbx',
      role: 'FreeSWITCH nodo 1, solo LAN; signaling e media pubblici attraverso KAM-1',
      publicIp: null,
      exposure: 'lan-only',
      lanIp: '10.4.202.51',
      sshSecret: 'SSH_KEY_FS1_B64',
      status: 'ha',
      services: [
        'FreeSWITCH (ESL :8021)',
        'audio-syncer (pull R2 → sounds locali; NFS dismesso)',
        'fail2ban + agent',
      ],
      runbooks: ['gotchas-deploy.md', 'cluster-topology.md'],
      notes: 'Nessun IP pubblico, backend Node o HAProxy DB. Ingress SIP dal dispatcher KAM-1, outbound PSTN attraverso KAM-1; media via rtpengine. Accesso amministrativo tramite bastion.',
    },
    {
      id: 'fs2',
      label: 'FS-2',
      tier: 'pbx',
      role: 'FreeSWITCH nodo 2, solo LAN; peer attivo nel dispatcher di KAM-1',
      publicIp: null,
      exposure: 'lan-only',
      lanIp: '10.4.202.61',
      sshSecret: 'SSH_KEY_FS2_B64',
      status: 'ha',
      services: [
        'FreeSWITCH (ESL :8021)',
        'audio-syncer (pull R2 → sounds locali; NFS dismesso)',
        'fail2ban + agent',
      ],
      runbooks: ['gotchas-deploy.md', 'cluster-topology.md'],
      notes: 'Nessun IP pubblico. Dispatcher setid=1, affinità tenant; SIP e RTP pubblici passano da KAM-1. Accesso amministrativo tramite bastion.',
    },
    {
      id: 'be1',
      label: 'BE-1',
      tier: 'app',
      role: 'Backend applicativo: 8 worker PM2 fork, porte 3000–3007; consumer ESL FS-1 e FS-2',
      publicIp: null,
      exposure: 'lan-only',
      lanIp: '10.4.202.58',
      sshSecret: 'SSH_KEY_BE1_B64',
      status: 'ha',
      services: [
        'PM2 voip-backend-0..7 (:3000–3007), 8 worker attivi',
        '2 connessioni ESL per worker (FS-1 + FS-2)',
        'fail2ban + agent',
        'HAProxy locale :5432/:5433',
      ],
      runbooks: ['gotchas-deploy.md', 'fail2ban-cluster.md'],
      notes: 'Solo LAN, dietro LB-1. SERVER_ROLE=primary; insieme a BE-2 forma il pool applicativo a 16 worker. Nessun server LiveKit locale.',
    },
    {
      id: 'be2',
      label: 'BE-2',
      tier: 'app',
      role: 'Backend applicativo: 8 worker PM2 fork, porte 3000–3007; consumer ESL FS-1 e FS-2',
      publicIp: null,
      exposure: 'lan-only',
      lanIp: '10.4.202.52',
      sshSecret: 'SSH_KEY_BE2_B64',
      status: 'ha',
      services: [
        'PM2 voip-backend-0..7 (:3000–3007), 8 worker attivi',
        '2 connessioni ESL per worker (FS-1 + FS-2)',
        'fail2ban + agent',
        'HAProxy locale :5432/:5433',
      ],
      runbooks: ['gotchas-deploy.md', 'fail2ban-cluster.md'],
      notes: 'Solo LAN, dietro LB-1. SERVER_ROLE=api; insieme a BE-1 forma il pool applicativo a 16 worker. Nessun server LiveKit locale.',
    },
    {
      id: 'kam1',
      label: 'KAM-1',
      tier: 'sbc',
      role: 'Ingresso SIP pubblico: Kamailio dispatcher + WSS nativo; rtpengine per il relay RTP',
      publicIp: '85.94.215.109',
      exposure: 'public',
      lanIp: '10.4.202.59',
      sshSecret: 'SSH_KEY_KAM1_B64',
      status: 'single',
      services: [
        'kamailio (SIP UDP/TCP :5060, SIP TLS :5061)',
        'kamailio mod_websocket+mod_tls (WSS :8443 path /sip)',
        'dispatcher → FS-1/FS-2 (setid=1 sticky)',
        'rtpengine (RTP UDP :16384–32768, interfacce LAN/pubblica)',
        'acme.sh CF DNS-01 auto-renew (SAN wss+sip+ws)',
        'fail2ban + agent',
      ],
      runbooks: ['kamailio-dispatcher-sync.md', 'sip-security-layers.md', 'cluster-topology.md'],
      notes: 'SIP e media voce non attraversano il bilanciatore web LB-1. KAM-1 espone UDP/TCP :5060, TLS :5061, WSS :8443 e RTP; FS-1/FS-2 restano solo LAN. Ingresso SSH di fallback quando OBS-1 non ha WAN configurata.',
    },
    {
      id: 'db1',
      label: 'DB-1',
      tier: 'db',
      role: 'PostgreSQL 16 / Patroni: nodo leader indicato nell’inventario, membro etcd',
      publicIp: null,
      exposure: 'lan-only',
      lanIp: '10.4.202.53',
      sshSecret: 'SSH_KEY_DB1_B64',
      status: 'leader',
      services: ['postgres 16', 'patroni 3.3', 'etcd member', 'fail2ban + agent'],
      runbooks: ['postgres-ha-patroni.md', 'db-roles-governance.md', 'db-backup-restore.md'],
      notes: 'Schema gestito da efai_owner/dbmate. Backup pgBackRest su R2 con archivio WAL e ripristino PITR. Ruolo leader da verificare live: Patroni può cambiarlo durante un failover. Accesso tramite bastion.',
    },
    {
      id: 'db2',
      label: 'DB-2',
      tier: 'db',
      role: 'PostgreSQL 16 / Patroni: replica streaming indicata nell’inventario, membro etcd',
      publicIp: null,
      exposure: 'lan-only',
      lanIp: '10.4.202.56',
      sshSecret: 'SSH_KEY_DB2_B64',
      status: 'replica',
      services: ['postgres 16', 'patroni 3.3', 'etcd member', 'fail2ban + agent'],
      runbooks: ['postgres-ha-patroni.md'],
      notes: 'Read pool via HAProxy :5433 e health check Patroni :8008. Può essere promosso leader: la mappa è un inventario, non il monitor live del ruolo. Accesso tramite bastion.',
    },
    {
      id: 'redis1',
      label: 'REDIS-1',
      tier: 'cache',
      role: 'Redis dedicato (DB 0-7 split) + etcd + fail2ban+agent',
      publicIp: null,
      exposure: 'lan-only',
      lanIp: '10.4.202.57',
      sshSecret: 'SSH_KEY_REDIS1_B64',
      status: 'single',
      services: ['redis-server', 'etcd member', 'fail2ban + agent', 'backup RDB+ACL → R2 age'],
      runbooks: ['redis.md'],
      notes: 'DB split per ruolo: 0 default, 1 bullmq, 2 cache, 3 idem, 4 ratelimit, 5 livekit, 6 socketio, 7 voicecounter. Nessun IP pubblico; accesso tramite bastion.',
    },
    {
      id: 'be3',
      label: 'BE-3',
      tier: 'video',
      role: 'LiveKit cluster node 1 (Video self-hosted) + Caddy TLS + agent worker',
      publicIp: '85.94.215.107',
      exposure: 'public',
      lanIp: '10.4.202.54',
      sshSecret: 'SSH_KEY_BE3_B64',
      status: 'ha',
      services: ['livekit-server :7880', 'Caddy :443 (TLS)', 'livekit-egress', 'livekit-agent-worker', 'fail2ban + agent'],
      runbooks: ['livekit-cluster.md', 'livekit-agent-worker.md', 'livekit-smoke-e2e.md'],
      notes: 'Video SOLO qui + BE-4 (vincolo non negoziabile). Endpoint pubblico wss://media.easyflowai.it (round-robin media-1/media-2).',
    },
    {
      id: 'be4',
      label: 'BE-4',
      tier: 'video',
      role: 'LiveKit cluster node 2 (HA) + Caddy TLS + agent worker',
      publicIp: '85.94.215.108',
      exposure: 'public',
      lanIp: '10.4.202.55',
      sshSecret: 'SSH_KEY_BE4_B64',
      status: 'ha',
      services: ['livekit-server :7880', 'Caddy :443 (TLS)', 'livekit-egress', 'livekit-agent-worker', 'fail2ban + agent'],
      runbooks: ['livekit-cluster.md', 'livekit-agent-worker.md'],
    },
    {
      id: 'obs1',
      label: 'OBS-1',
      tier: 'obs',
      role: 'Osservabilità e gestione cluster sulla LAN; bastion operativo previsto dalla convenzione SSH',
      publicIp: null,
      exposure: 'lan-only',
      lanIp: '10.4.202.60',
      sshSecret: 'SSH_KEY_OBS1_B64',
      status: 'single',
      services: ['Prometheus + Grafana + Alertmanager + blackbox exporter', 'GlitchTip (error tracking applicativo)', 'bastion e gestione operativa', 'fail2ban + agent'],
      runbooks: ['ssh-keys-audit.md', 'observability-cluster.md', 'observability-otel.md'],
      notes: 'Nessun IP pubblico nell’inventario. Pagine web di monitoraggio via LB-1 → OBS-1 LAN. Il wrapper SSH usa KAM-1 come ingresso se non è configurato un WAN per OBS-1.',
    },
  ],
  edges: [
    // Edge web pubblico LB-1 → origin
    { id: 'web-lb1-fe1', from: 'lb1', to: 'fe1', kind: 'app-traffic', label: 'HTTP :8081 statici (dist tenant+master)' },
    { id: 'web-lb1-be1', from: 'lb1', to: 'be1', kind: 'app-traffic', label: 'API/Socket.IO → 8 worker :3000–3007' },
    { id: 'web-lb1-be2', from: 'lb1', to: 'be2', kind: 'app-traffic', label: 'API/Socket.IO → 8 worker :3000–3007' },
    { id: 'web-lb1-obs1', from: 'lb1', to: 'obs1', kind: 'app-traffic', label: 'Grafana / GlitchTip / DB studio via LAN' },
    { id: 'f2b-lb1-be1', from: 'lb1', to: 'be1', kind: 'control-plane', label: 'agent → receiver BE, heartbeat 30s' },

    // Replication + control plane DB
    { id: 'patroni-stream', from: 'db1', to: 'db2', kind: 'replication', label: 'replica streaming Patroni (ruoli da inventario)' },
    { id: 'etcd-db1-db2', from: 'db1', to: 'db2', kind: 'etcd', label: 'etcd peer :2380' },
    { id: 'etcd-db1-redis1', from: 'db1', to: 'redis1', kind: 'etcd', label: 'etcd peer :2380' },
    { id: 'etcd-db2-redis1', from: 'db2', to: 'redis1', kind: 'etcd', label: 'etcd peer :2380' },

    // App → DB
    { id: 'app-be1-db', from: 'be1', to: 'db1', kind: 'app-traffic', label: 'HAProxy :5432 → leader' },
    { id: 'app-be2-db', from: 'be2', to: 'db1', kind: 'app-traffic', label: 'HAProxy :5432 → leader' },
    { id: 'app-be1-replica', from: 'be1', to: 'db2', kind: 'app-traffic', label: 'HAProxy :5433 → replica (analytics)' },
    { id: 'app-be2-replica', from: 'be2', to: 'db2', kind: 'app-traffic', label: 'HAProxy :5433 → replica (analytics)' },

    // Cache Redis
    { id: 'cache-be1', from: 'be1', to: 'redis1', kind: 'cache', label: 'Redis :6379 (DB 0-4,6,7)' },
    { id: 'cache-be2', from: 'be2', to: 'redis1', kind: 'cache', label: 'Redis :6379 (DB 0-4,6,7)' },
    { id: 'cache-be3', from: 'be3', to: 'redis1', kind: 'cache', label: 'Redis DB 5 (ACL livekit_cluster)' },
    { id: 'cache-be4', from: 'be4', to: 'redis1', kind: 'cache', label: 'Redis DB 5 (ACL livekit_cluster)' },

    // SIP (KAM-1 dispatcher → FS-1/FS-2)
    { id: 'sip-kam-fs1', from: 'kam1', to: 'fs1', kind: 'sip', label: 'dispatcher sticky-tenant (setid=1 AP)' },
    { id: 'sip-kam-fs2', from: 'kam1', to: 'fs2', kind: 'sip', label: 'dispatcher sticky-tenant (setid=1 AP)' },

    // ESL backend → FreeSWITCH
    { id: 'esl-be1-fs1', from: 'be1', to: 'fs1', kind: 'esl', label: 'ESL :8021 consumer' },
    { id: 'esl-be2-fs1', from: 'be2', to: 'fs1', kind: 'esl', label: 'ESL :8021 consumer' },
    { id: 'esl-be1-fs2', from: 'be1', to: 'fs2', kind: 'esl', label: 'ESL :8021 consumer' },
    { id: 'esl-be2-fs2', from: 'be2', to: 'fs2', kind: 'esl', label: 'ESL :8021 consumer' },
    { id: 'rtp-kam-fs1', from: 'kam1', to: 'fs1', kind: 'media', label: 'rtpengine ↔ FreeSWITCH, gamba LAN' },
    { id: 'rtp-kam-fs2', from: 'kam1', to: 'fs2', kind: 'media', label: 'rtpengine ↔ FreeSWITCH, gamba LAN' },

    // Media WebRTC LiveKit
    { id: 'media-be3-be4', from: 'be3', to: 'be4', kind: 'media', label: 'LiveKit HA (wss://media.easyflowai.it)' },
    { id: 'media-be1-be3', from: 'be1', to: 'be3', kind: 'media', label: 'server-sdk consumer' },
    { id: 'media-be2-be4', from: 'be2', to: 'be4', kind: 'media', label: 'server-sdk consumer' },

    // Gli agent inviano il heartbeat al receiver applicativo, non a FE-1.
    { id: 'f2b-be1-fe1', from: 'fe1', to: 'be1', kind: 'control-plane', label: 'agent → receiver BE, heartbeat 30s' },
    { id: 'f2b-be1-be2', from: 'be2', to: 'be1', kind: 'control-plane', label: 'agent → receiver BE, heartbeat 30s' },
    { id: 'f2b-be1-fs1', from: 'fs1', to: 'be1', kind: 'control-plane', label: 'agent → receiver BE, heartbeat 30s' },
    { id: 'f2b-be1-fs2', from: 'fs2', to: 'be1', kind: 'control-plane', label: 'agent → receiver BE, heartbeat 30s' },
    { id: 'f2b-be1-kam1', from: 'kam1', to: 'be1', kind: 'control-plane', label: 'agent → receiver BE, heartbeat 30s' },
    { id: 'f2b-be1-db1', from: 'db1', to: 'be1', kind: 'control-plane', label: 'agent → receiver BE, heartbeat 30s' },
    { id: 'f2b-be1-db2', from: 'db2', to: 'be1', kind: 'control-plane', label: 'agent → receiver BE, heartbeat 30s' },
    { id: 'f2b-be1-redis1', from: 'redis1', to: 'be1', kind: 'control-plane', label: 'agent → receiver BE, heartbeat 30s' },
    { id: 'f2b-be1-be3', from: 'be3', to: 'be1', kind: 'control-plane', label: 'agent → receiver BE, heartbeat 30s' },
    { id: 'f2b-be1-be4', from: 'be4', to: 'be1', kind: 'control-plane', label: 'agent → receiver BE, heartbeat 30s' },
    { id: 'f2b-be1-obs1', from: 'obs1', to: 'be1', kind: 'control-plane', label: 'agent → receiver BE, heartbeat 30s' },
  ],
  edgeLegend: [
    { kind: 'replication', label: 'Replication Patroni leader→replica', color: '#10b981' },
    { kind: 'etcd', label: 'etcd peer (control plane DCS)', color: '#94a3b8', dash: '4 3' },
    { kind: 'app-traffic', label: 'Web/API via LB-1 e accesso DB via HAProxy', color: '#6366f1' },
    { kind: 'cache', label: 'Cache Redis :6379', color: '#f59e0b' },
    { kind: 'sip', label: 'SIP dispatcher Kamailio → FreeSWITCH', color: '#a855f7' },
    { kind: 'esl', label: 'ESL backend Node → FreeSWITCH :8021', color: '#0ea5e9' },
    { kind: 'media', label: 'Media voce rtpengine / video LiveKit', color: '#ec4899' },
    { kind: 'control-plane', label: 'Agent → receiver backend (heartbeat 30s)', color: '#64748b', dash: '2 3' },
  ],
  statusLegend: [
    { status: 'leader', label: 'Leader Patroni (R/W)' },
    { status: 'replica', label: 'Replica streaming async' },
    { status: 'ha', label: 'High Availability (peer attivo-attivo)' },
    { status: 'single', label: 'Single instance (SPOF accettato)' },
  ],
  stack: [
    'nginx LB-1: ingresso web pubblico, TLS, API e Socket.IO; origin statico FE-1 :8081 LAN',
    'React 18 + Vite (frontend tenant, master, W3 e Brand; dist su FE-1, nessun Node sul frontend)',
    'Express.js + Node 22.22: PM2 fork 8+8 su BE-1/BE-2, 16 worker e 16 endpoint :3000–3007',
    'FreeSWITCH cluster FS-1+FS-2 (sounds via audio-syncer R2, NFS dismesso)',
    'Kamailio SBC KAM-1 + rtpengine (SIP/WSS pubblici, dispatcher sticky-tenant, relay RTP)',
    'PostgreSQL 16 + Patroni 3.3 + etcd 3-nodi',
    'Redis dedicato (REDIS-1) DB split 0-7',
    'LiveKit self-hosted (BE-3/BE-4) + Caddy TLS',
    'OBS-1: Prometheus, Grafana, Alertmanager e GlitchTip; accesso web tramite LB-1, SSH con fallback KAM-1',
    'Backup PostgreSQL: pgBackRest su R2, archivio WAL e ripristino PITR',
    'Socket.IO + Redis adapter (REDIS-1 DB 6)',
    'Tailwind CSS + design tokens',
  ],
  constraints: [
    {
      title: 'Web pubblico SOLO su LB-1',
      detail: 'LB-1 termina TLS e inoltra statici a FE-1 :8081, API/Socket.IO ai 16 worker BE-1/BE-2 e monitoraggio a OBS-1 LAN. FE-1 non è più origin DNS pubblico: il suo IP WAN è di gestione.',
    },
    {
      title: 'Nodi interni senza IP pubblico',
      detail: 'BE-1, BE-2, FS-1, FS-2, DB-1, DB-2, REDIS-1 e OBS-1 sono solo LAN. SIP/media voce entrano da KAM-1; LiveKit da BE-3/BE-4. Rete e accesso amministrativo non equivalgono a esposizione web.',
    },
    {
      title: 'Video SOLO su BE-3/BE-4',
      detail: 'Vietato installare livekit-server/egress/agent/ingress su BE-1/BE-2/FE-1/DB-1. Backend Node è SOLO consumer client (livekit-server-sdk). Endpoint pubblico wss://media.easyflowai.it. Provider rifiuta esplicitamente *.livekit.cloud.',
    },
    {
      title: 'SIP SOLO su KAM-1 → FS-1/FS-2',
      detail: 'Tutto il traffico SIP pubblico (UDP/TCP :5060, TLS :5061, WSS :8443 path /sip) entra esclusivamente da Kamailio KAM-1, che dispatcher sticky-tenant verso FS-1/FS-2 (setid=1 AP, hash $tenant_id). freeswitch.service inactive sui non-FS.',
    },
    {
      title: 'Ruoli Postgres efai_*',
      detail: 'Runtime backend usa efai_app (DML, no CREATE). Analytics usa efai_ro (SELECT-only). Dbmate usa efai_ddl (IN ROLE efai_owner). Owner logico efai_owner NOLOGIN. Vietato edgvoip/postgres come runtime.',
    },
    {
      title: 'FORCE RLS attivo',
      detail: 'Le tabelle tenant-scoped sono protette da FORCE ROW LEVEL SECURITY e gate di copertura. Il contesto tenant viene applicato prima delle query; con contesto mancante le policy null-safe filtrano a zero righe. Il numero delle tabelle dipende dallo schema corrente.',
    },
    {
      title: 'Schema DB solo via dbmate',
      detail: 'Vietata DDL inline dal codice (services/routes/jobs/boot/cron). Hard gate CI: db:check-no-inline-ddl + db:check-rls-coverage. Migration in packages/database/db/migrations/.',
    },
    {
      title: 'Routing tenant via JWT (rotte nuove)',
      detail: 'Vietato montare nuove rotte sotto tenantScopedRouter o /api/:tenantSlug/. Rotte nuove: protectedRouter o top-level /api/<segment>. Frontend: chiamate via apiClient con whitelist TENANT_AGNOSTIC_PREFIXES.',
    },
    {
      title: 'No SSH cross-node dal codice',
      detail: 'I comandi applicativi cross-nodo passano per internalLanClient (HTTP interno, chiave + JWT), non per credenziali SSH. Per operazioni umane usare ssh-prod.sh: OBS-1 è il bastion preferito se ha WAN configurata, altrimenti ingresso KAM-1.',
    },
    {
      title: 'UI nei package consentiti',
      detail: 'Le superfici React vivono nei package frontend dedicati (tenant, master, W3, Brand) e nelle librerie UI condivise, non nel backend o negli script operativi.',
    },
    {
      title: 'No `any` / `@ts-ignore`',
      detail: 'Tipizzazione esplicita obbligatoria. Vietato any, as any, @ts-ignore senza giustificazione documentata. Interface/type/zod schema/generics. Lint gate hard CI.',
    },
  ],
  runbookIndex: [
    { title: 'Cluster topology (source of truth)', path: 'docs/runbooks/cluster-topology.md' },
    { title: 'LB-1 edge web', path: 'docs/runbooks/lb-vps-setup.md' },
    { title: 'Osservabilità cluster', path: 'docs/runbooks/observability-cluster.md' },
    { title: 'Postgres HA Patroni', path: 'docs/runbooks/postgres-ha-patroni.md' },
    { title: 'DB roles governance (efai_*)', path: 'docs/runbooks/db-roles-governance.md' },
    { title: 'DB backup & restore', path: 'docs/runbooks/db-backup-restore.md' },
    { title: 'Redis dedicato', path: 'docs/runbooks/redis.md' },
    { title: 'Kamailio dispatcher sync', path: 'docs/runbooks/kamailio-dispatcher-sync.md' },
    { title: 'SIP security layers', path: 'docs/runbooks/sip-security-layers.md' },
    { title: 'LiveKit cluster Video', path: 'docs/runbooks/livekit-cluster.md' },
    { title: 'LiveKit agent worker', path: 'docs/runbooks/livekit-agent-worker.md' },
    { title: 'Fail2Ban cluster', path: 'docs/runbooks/fail2ban-cluster.md' },
    { title: 'SSH keys audit', path: 'docs/runbooks/ssh-keys-audit.md' },
    { title: 'Deploy & infra', path: 'docs/runbooks/gotchas-deploy.md' },
    { title: 'DB / runtime', path: 'docs/runbooks/gotchas-db-runtime.md' },
    { title: 'Lint rules architetturali', path: 'docs/runbooks/lint-rules.md' },
  ],
  publicEndpoints: [
    { label: 'Frontend tenant (apex)', value: 'https://easyflowai.it' },
    { label: 'Frontend master', value: getDefaultMasterOrigin() },
    { label: 'EasyCRM', value: getDefaultCrmOrigin() },
    ...PRODUCT_REGISTRY.filter((product) => product.key !== 'easyflow').map((product) => ({
      label: `Frontend ${product.label}`,
      value: `https://${product.publicHosts[0]}`,
    })),
    { label: 'App non-browser (Android/iOS/B2B)', value: 'https://app.easyflowai.it' },
    { label: 'API + Webhook esterni', value: 'https://easyflowai.it/api/' },
    { label: 'Socket.IO browser', value: 'wss://ws.easyflowai.it' },
    { label: 'SIP WebRTC (KAM-1)', value: 'wss://wss.easyflowai.it:8443/sip' },
    { label: 'SIP UDP/TCP (KAM-1)', value: 'sip.easyflowai.it:5060' },
    { label: 'SIP TLS (KAM-1)', value: 'sip.easyflowai.it:5061' },
    { label: 'Video Call Live LiveKit', value: 'wss://media.easyflowai.it' },
    { label: 'Fiscal printer A-Cube', value: 'https://fiscal.easyflowai.it' },
  ],
};

export function tierColor(tierId: TierId): string {
  return CLUSTER_TOPOLOGY.tiers.find((t) => t.id === tierId)?.color ?? '#64748b';
}

export function findNode(id: string): ClusterNode | undefined {
  return CLUSTER_TOPOLOGY.nodes.find((n) => n.id === id);
}

export interface NodePosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface LayoutResult {
  width: number;
  height: number;
  nodePositions: Record<string, NodePosition>;
  tierBands: { id: TierId; x: number; y: number; w: number; h: number; label: string }[];
}

export function computeLayout(): LayoutResult {
  const tierOrder: TierId[] = ['app', 'pbx', 'sbc', 'db', 'cache', 'video', 'obs'];
  const nodeW = 168;
  const nodeH = 108;
  const tierGap = 12;
  const tierPadX = 10;
  const tierPadTop = 36;
  const tierPadBottom = 12;
  const nodeGapY = 14;

  const tierNodeCount: Record<TierId, number> = {
    app: 0, pbx: 0, sbc: 0, db: 0, cache: 0, video: 0, obs: 0,
  };
  for (const n of CLUSTER_TOPOLOGY.nodes) {
    tierNodeCount[n.tier]++;
  }
  const maxNodes = Math.max(...tierOrder.map((t) => tierNodeCount[t]));

  const tierW = nodeW + tierPadX * 2;
  const tierH = tierPadTop + tierPadBottom + maxNodes * nodeH + (maxNodes - 1) * nodeGapY;
  const width = 20 + tierOrder.length * (tierW + tierGap);
  const height = 30 + tierH + 20;

  const positions: Record<string, NodePosition> = {};
  const bands: LayoutResult['tierBands'] = [];

  tierOrder.forEach((tierId, idx) => {
    const xBand = 20 + idx * (tierW + tierGap);
    const yBand = 30;
    const tier = CLUSTER_TOPOLOGY.tiers.find((t) => t.id === tierId);
    bands.push({
      id: tierId,
      x: xBand,
      y: yBand,
      w: tierW,
      h: tierH,
      label: tier?.label ?? tierId,
    });
    const tierNodes = CLUSTER_TOPOLOGY.nodes.filter((n) => n.tier === tierId);
    tierNodes.forEach((node, nIdx) => {
      positions[node.id] = {
        x: xBand + tierPadX,
        y: yBand + tierPadTop + nIdx * (nodeH + nodeGapY),
        w: nodeW,
        h: nodeH,
      };
    });
  });

  return { width, height, nodePositions: positions, tierBands: bands };
}
